const _excluded = ["className", "complete", "invalid"],
  _excluded2 = ["valueState"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { forwardRef, useMemo, useReducer, useState } from 'react';
import cn from 'classnames';
import { Button, Callout, Typography, useOverlayContext } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import typography from 'seagull/foundations/typography.module.scss';
import { useCobBranding } from "../../client-flow/clientOb/CobBrandingContext";
import { createPaymentSetupHandlers } from "./ManagePaymentMethods.utils";
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { Intent } from "../../infra/access-control";
import { handleRequest, isNonEmptyInput } from "../../infra/utils";
import { RelationshipProgressStatus } from "../../models/relationship";
import { selectors } from "../../store/state-selectors";
import { SlackUserGroups } from "../../utils/slack-client";
import styles from "./ConnectCreditCard.module.scss";
import { getGeneralErrorText } from "./errors";
import mpmStyles from "./ManagePaymentMethods.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const emptyExpiryError = 'Please enter a valid card expiry date';
const emptyCvvError = 'Please enter a valid CVV';
const emptyCardNumberError = 'Please enter a valid card number';

// https://stripe.com/docs/js/elements_object/create_element?type=card
const classesObj = {
  complete: mpmStyles.complete
};
const getInitialValueState = () => ({
  value: '',
  error: null,
  pristine: true,
  touched: false
});
const getInitialStripeValue = function getInitialStripeValue() {
  let errorMessage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return {
    touched: false,
    valid: false,
    errorMessage
  };
};
function mapStripeErrorToMessage(error) {
  switch (error.code) {
    case 'card_declined':
      {
        switch (error.decline_code) {
          case 'invalid_account':
            return 'Credit card connection failed. The card, or account the card is connected to, is invalid. Contact your card issuer or connect a different payment method.';
          case 'transaction_not_allowed':
          case 'do_not_honor':
            return 'The card was declined. Contact your card issuer to find out why it was declined or connect a different payment method.';
          default:
            return null;
        }
      }
    case 'incorrect_cvc':
    case 'invalid_cvc':
      return "The card's security code is incorrect. Check the card's security code or use a different card.";
    case 'setup_intent_authentication_failure':
      return "The provided credit card has failed authentication. Please check the card's information or connect a different payment method.";
    case 'incorrect_number':
      return "The card number is incorrect. Check the card's number or use a different card.";
    default:
      return null;
  }
}
const stripeStateReducer = (state, action) => {
  const {
    type,
    errorMessage = ''
  } = action;
  switch (type) {
    case 'error':
      return _objectSpread(_objectSpread({}, state), {}, {
        valid: false,
        errorMessage
      });
    case 'valid':
      return state.valid ? state : _objectSpread(_objectSpread({}, state), {}, {
        valid: true
      });
    case 'blur':
      return state.touched ? state : _objectSpread(_objectSpread({}, state), {}, {
        touched: true
      });
    default:
      return state;
  }
};
function validateNameOnCard(value) {
  return isNonEmptyInput(value) ? '' : 'Please specify the name on the card.';
}
function validateZipCode(value) {
  let minLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 5;
  value = value.trim();
  let error = '';
  if (!isNonEmptyInput(value)) {
    error = 'Please specify the ZIP code.';
  } else if (value.length < minLength) {
    error = 'Please enter a valid 5-digit ZIP Code';
  }
  return error;
}
const Label = props => {
  const {
    children,
    size = 'small'
  } = props;
  return /*#__PURE__*/_jsx(Typography, {
    className: cn(mpmStyles.label, {
      [mpmStyles.mediumSize]: size === 'medium'
    }),
    variant: size === 'small' ? 'paragraph2' : 'paragraph1',
    children: children
  });
};
const Field = props => /*#__PURE__*/_jsx("div", {
  className: cn(mpmStyles.field, {
    [mpmStyles.marginTop]: !props.isFirst,
    [mpmStyles.largeMargin]: props.largeMargin
  }, props.className),
  children: props.children
});
const ErrorText = props => /*#__PURE__*/_jsx(Typography, _objectSpread(_objectSpread({}, props), {}, {
  className: cn(mpmStyles.error, props.className),
  variant: "paragraph2"
}));
const BaseInput = /*#__PURE__*/forwardRef(function Input(props, ref) {
  const {
      className,
      complete,
      invalid
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/_jsx("input", _objectSpread(_objectSpread({}, rest), {}, {
    className: cn(mpmStyles.input, {
      [mpmStyles.complete]: complete,
      [mpmStyles.invalid]: invalid
    }, className),
    ref: ref
  }));
});
const Input = props => {
  const {
      valueState
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  return /*#__PURE__*/_jsx(BaseInput, _objectSpread(_objectSpread({}, rest), {}, {
    complete: valueState.touched && !valueState.error,
    invalid: valueState.touched && !!valueState.error,
    value: valueState.value
  }));
};

/**
 * Renders a form for filling out credit-card details.
 * Expects to be wrapped in an Elements provider from @stripe/react-stripe-js.
 */
export default function ConnectCreditCard(props) {
  const {
    relationshipId,
    vendorId,
    customerId,
    disabledReason,
    updateProgress,
    onConnect,
    doPaymentMethodAction
  } = props;
  const {
    branding
  } = useCobBranding();
  const {
    slackClient: {
      sendCsNotificationsMessage
    },
    reactStripe: {
      useElements,
      useStripe,
      CardNumberElement,
      CardExpiryElement,
      CardCvcElement
    }
  } = useIoContext();
  const elements = useElements();
  const stripe = useStripe();
  const overlayContext = useOverlayContext();
  const isVendor = useStore(selectors.user.isVendor);
  const {
    api,
    logBI
  } = useStoreActions();
  const stripeLoadingFailed = useStore(state => state.stripeLoadingFailed);
  const [cardNum, cardNumDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyCardNumberError));
  const [expiry, expiryDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyExpiryError));
  const [cvv, cvvDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyCvvError));
  const [nameOnCard, setNameOnCard] = useState(getInitialValueState);
  const [zipCode, setZipCode] = useState(getInitialValueState);
  const [apiError, setApiError] = useState();
  const [submitting, setSubmitting] = useState(false);
  const allFieldsValid = cardNum.valid && expiry.valid && cvv.valid && !nameOnCard.pristine && !nameOnCard.error && !zipCode.pristine && !zipCode.error;

  // https://stripe.com/docs/js/appendix/style
  const styleObj = useMemo(() => ({
    base: {
      fontFamily: typography.fontFamilyLight,
      fontSize: typography.fontSizeP1,
      fontSmoothing: 'antialiased',
      color: colors.dark700,
      ':-webkit-autofill': {
        color: colors.dark700
      },
      '::placeholder': {
        color: colors.dark700
      }
    },
    invalid: {
      color: colors.dark700
    }
  }), [branding]);
  async function connectCreditCard() {
    setApiError(undefined);
    if (!allFieldsValid || !elements || !stripe) return;
    const cardNumberEl = elements.getElement('cardNumber');
    if (!cardNumberEl) return;
    logBI(10806);
    const loaderName = 'creditCard';
    setSubmitting(true);
    overlayContext.startLoader(loaderName);
    const handleError = message => {
      setSubmitting(false);
      overlayContext.stopLoader(loaderName);
      setApiError(message != null ? message : getGeneralErrorText());
      logBI(10819);
    };
    const {
      createSetupIntent,
      addToBusiness
    } = createPaymentSetupHandlers({
      isVendor,
      paymentMethodHandlers: api.paymentMethod,
      vendorId,
      customerId
    });
    const clientSecret = await handleRequest(createSetupIntent());
    if (!clientSecret.success) {
      return handleError();
    }
    const setupIntentResult = await stripe.confirmCardSetup(clientSecret.data, {
      payment_method: {
        card: cardNumberEl,
        billing_details: {
          name: nameOnCard.value,
          address: {
            postal_code: zipCode.value
          }
        }
      }
    });
    if (setupIntentResult.error) {
      const mappedMessage = mapStripeErrorToMessage(setupIntentResult.error);
      if (mappedMessage == null && setupIntentResult.error.code) {
        const text = "add credit-card error (relationshipId=".concat(relationshipId, ", vendorId=").concat(vendorId, ") - ").concat(JSON.stringify(setupIntentResult.error), " - ").concat(SlackUserGroups.ProductTeam, " please provide texts for this failure :pray:");
        sendCsNotificationsMessage(text);
      }
      logBI(10007, {
        success: false
      });
      return handleError(mappedMessage);
    }
    logBI(10007, {
      success: true
    });
    const paymentMethodId = setupIntentResult.setupIntent.payment_method;
    if (!paymentMethodId) {
      return handleError();
    }
    const fundingSource = await handleRequest(addToBusiness(paymentMethodId));
    if (!fundingSource.success) {
      return handleError();
    }
    if (updateProgress) {
      api.relationship.updateProgressStatus(relationshipId, RelationshipProgressStatus.PaymentMethod);
    }
    logBI(10808);
    overlayContext.stopLoader(loaderName);
    setSubmitting(false);
    onConnect(fundingSource.data.id);
  }
  return /*#__PURE__*/_jsxs("form", {
    onSubmit: e => {
      e.preventDefault();
      doPaymentMethodAction(connectCreditCard);
    },
    children: [/*#__PURE__*/_jsxs(Field, {
      isFirst: true,
      children: [/*#__PURE__*/_jsx(Label, {
        children: "Name on card"
      }), /*#__PURE__*/_jsx(Input, {
        valueState: nameOnCard,
        placeholder: "Enter your name",
        "data-testid": "manage-payment-methods-credit-card-name",
        className: cn(mpmStyles.input, {
          [mpmStyles.invalid]: nameOnCard.touched && nameOnCard.error
        }),
        disabled: !!disabledReason,
        onChange: ev => {
          setApiError(undefined);
          const error = validateNameOnCard(ev.currentTarget.value);
          setNameOnCard(_objectSpread(_objectSpread({}, nameOnCard), {}, {
            value: ev.currentTarget.value,
            pristine: false,
            error
          }));
        },
        onBlur: () => {
          const error = validateNameOnCard(nameOnCard.value);
          setNameOnCard(_objectSpread(_objectSpread({}, nameOnCard), {}, {
            error,
            touched: true
          }));
        }
      }), nameOnCard.error && nameOnCard.touched && /*#__PURE__*/_jsx(ErrorText, {
        "data-testid": "connect-cc-name-error",
        children: nameOnCard.error
      })]
    }), /*#__PURE__*/_jsxs(Field, {
      children: [/*#__PURE__*/_jsx(Label, {
        children: "Card number"
      }), /*#__PURE__*/_jsx(CardNumberElement, {
        className: cn(mpmStyles.input, {
          [mpmStyles.invalid]: cardNum.touched && !cardNum.valid && cardNum.errorMessage
        }),
        options: {
          disabled: !!disabledReason,
          style: styleObj,
          classes: classesObj,
          placeholder: '0000-0000-0000-0000'
        },
        onChange: event => {
          const {
            complete,
            error,
            empty
          } = event;
          setApiError(undefined);
          if (complete) {
            cardNumDispatch({
              type: 'valid'
            });
          } else {
            cardNumDispatch({
              type: 'error',
              errorMessage: error ? error.message : empty ? emptyCardNumberError : ''
            });
          }
        },
        onBlur: () => cardNumDispatch({
          type: 'blur'
        })
      }), cardNum.touched && !cardNum.valid && /*#__PURE__*/_jsx(ErrorText, {
        "data-testid": "connect-cc-num-error",
        children: cardNum.errorMessage
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.ccExpirationRow,
      children: [/*#__PURE__*/_jsxs(Field, {
        children: [/*#__PURE__*/_jsx(Label, {
          children: "Expiration date"
        }), /*#__PURE__*/_jsx(CardExpiryElement, {
          "data-testid": "manage-payment-methods-credit-card-expiry",
          className: cn(mpmStyles.input, {
            [mpmStyles.invalid]: expiry.touched && expiry.errorMessage
          }),
          options: {
            disabled: !!disabledReason,
            style: styleObj,
            classes: classesObj,
            placeholder: 'MM/YY'
          },
          onChange: event => {
            const {
              complete,
              error,
              empty
            } = event;
            setApiError(undefined);
            if (complete) {
              expiryDispatch({
                type: 'valid'
              });
            } else {
              expiryDispatch({
                type: 'error',
                errorMessage: error ? error.message : empty ? emptyExpiryError : ''
              });
            }
          },
          onBlur: () => expiryDispatch({
            type: 'blur'
          })
        }), expiry.touched && !expiry.valid && /*#__PURE__*/_jsx(ErrorText, {
          "data-testid": "connect-cc-expiry-error",
          children: expiry.errorMessage
        })]
      }), /*#__PURE__*/_jsxs(Field, {
        children: [/*#__PURE__*/_jsx(Label, {
          children: "CVV"
        }), /*#__PURE__*/_jsx(CardCvcElement, {
          className: cn(mpmStyles.input, {
            [mpmStyles.invalid]: cvv.touched && cvv.errorMessage
          }),
          options: {
            disabled: !!disabledReason,
            style: styleObj,
            classes: classesObj,
            placeholder: 'CVV'
          },
          onChange: event => {
            const {
              complete,
              error,
              empty
            } = event;
            setApiError(undefined);
            if (complete) {
              cvvDispatch({
                type: 'valid'
              });
            } else {
              cvvDispatch({
                type: 'error',
                errorMessage: error ? error.message : empty ? emptyCvvError : ''
              });
            }
          },
          onBlur: () => cvvDispatch({
            type: 'blur'
          })
        }), cvv.touched && !cvv.valid && /*#__PURE__*/_jsx(ErrorText, {
          "data-testid": "connect-cc-cvv-error",
          children: cvv.errorMessage
        })]
      }), /*#__PURE__*/_jsxs(Field, {
        children: [/*#__PURE__*/_jsx(Label, {
          children: "Zip code"
        }), /*#__PURE__*/_jsx(Input, {
          valueState: zipCode,
          type: "text",
          "data-testid": "connect-cc-zip",
          disabled: !!disabledReason,
          className: cn(mpmStyles.input, {
            [mpmStyles.invalid]: zipCode.touched && nameOnCard.error
          }),
          onChange: ev => {
            setApiError(undefined);
            const value = ev.target.value.trim();
            const error = validateZipCode(value, -1);
            setZipCode(_objectSpread(_objectSpread({}, zipCode), {}, {
              value,
              pristine: false,
              error
            }));
          },
          onBlur: () => {
            const error = validateZipCode(zipCode.value, -1);
            setZipCode(_objectSpread(_objectSpread({}, zipCode), {}, {
              touched: true,
              error
            }));
          }
        }), zipCode.error && zipCode.touched && /*#__PURE__*/_jsx(ErrorText, {
          "data-testid": "connect-cc-zip-error",
          children: zipCode.error
        })]
      })]
    }), /*#__PURE__*/_jsxs(Field, {
      largeMargin: true,
      children: [/*#__PURE__*/_jsx(Button, {
        type: "submit",
        size: "small",
        disabled: !!(!allFieldsValid || apiError || stripeLoadingFailed),
        loading: submitting,
        testId: "manage-payment-methods-add-credit-card",
        intent: Intent.AddPaymentMethod,
        tooltip: {
          placement: 'top',
          title: disabledReason ? /*#__PURE__*/_jsx("div", {
            "data-testid": "add-cc-disabled-reason",
            children: disabledReason
          }) : '',
          testId: 'connect-cc-submit-btn-tooltip',
          disableFocusListener: disabledReason == null,
          disableHoverListener: disabledReason == null,
          disableTouchListener: disabledReason == null
        },
        children: "Connect credit card"
      }), (apiError || stripeLoadingFailed) && /*#__PURE__*/_jsx(Callout, {
        className: styles.error,
        intent: "error",
        testId: "manage-payment-methods-cc-error",
        children: stripeLoadingFailed ? 'An error has occurred. Refresh the page and try again' : apiError
      })]
    })]
  });
}